import React, { useState } from 'react';
import { FormikValues, Field, FieldProps } from 'formik';
import {
  Select,
  TextField as ReactTextField,
  Alert as ReactAlert,
  Datepicker as ReactDatepicker,
} from '@a-ui/react';
import DataForm from '../../components/DataForm';
import { FormHelpers } from '../../components/Form/Form';
import Typography from '../../components/Typography';
import { api } from '../../hooks/api.hooks';
import usePersonalData, {
  CRS_PERSOON_KEYMAP,
  GENDER_OPTIONS,
  PersonalDataField,
  TRANSLATIONS,
} from '../../hooks/personalData.hooks';
import { translate } from '../../i18n';
import { useAuthentication } from '../../state/Authentication';
import { updateUser } from '../../state/Authentication/actions';
import { useMessage } from '../../state/Message';
import { setMessage } from '../../state/Message/actions';
import { validationSchema } from './config';

// eslint-disable-next-line prettier/prettier
interface PersonalDataProps { }

const PersonalData: React.FC<PersonalDataProps> = () => {
  const { state, dispatch } = useAuthentication();
  const [dateFieldValue, setDateFieldValue] = useState('');

  const { dispatch: dispatchMessage } = useMessage();
  const { data, humanReadableData, hasEID } = usePersonalData(state, [
    PersonalDataField.FIRST_NAME,
    PersonalDataField.LAST_NAME,
    PersonalDataField.NICKNAME,
    PersonalDataField.GENDER,
    PersonalDataField.BIRTH_DATE,
  ]);

  const handleSubmit = React.useCallback(
    async (values: any, { setFieldError }: FormHelpers) => {
      try {
        const filteredValues = Object.keys(values).reduce((result, key) => {
          if (hasEID && Object.keys(CRS_PERSOON_KEYMAP).includes(key)) {
            return result;
          }

          return {
            ...result,
            [key]: values[key],
          };
        }, {} as Record<string, any>);

        if (dateFieldValue && dateFieldValue !== '') {
          filteredValues.birthDate = dateFieldValue;
        }

        const { data: newUser } = await api.patch(`/me`, filteredValues);
        dispatch(updateUser(newUser));
        dispatchMessage(setMessage('data.form.save.success', 'dataOverview'));
      } catch (e) {
        setFieldError('', 'error.server.unknown');
      }
    },
    [hasEID, dateFieldValue],
  );

  const parseDate = (dateString: string) => {
    const [day, month, year] = dateString.split('/');

    // Handle dates with unknown day/month (e.g., "00/00/1962")
    if (day === '00' || month === '00') {
      // Return the string value for dates with 00
      return `${day}/${month}/${year}`;
    }

    return new Date(Number(year), Number(month) - 1, Number(day)); // Month is 0-indexed, so subtract 1 from the month.
  };

  return (
    <DataForm
      onSubmit={handleSubmit}
      data={humanReadableData}
      initialValues={data as FormikValues}
      validationSchema={validationSchema}
    >
      {hasEID && (
        <div className="u-margin-bottom">
          <ReactAlert inline>
            <Typography>{translate('personal_data.content.eid')}</Typography>
          </ReactAlert>
        </div>
      )}
      <div className="u-margin-bottom">
        <Field
          name="firstName"
          label={translate(TRANSLATIONS[PersonalDataField.FIRST_NAME]!)}
          disabled={hasEID}
          required={false}
          as={ReactTextField}
        />
      </div>

      <div className="u-margin-bottom">
        <Field
          label={translate(TRANSLATIONS[PersonalDataField.LAST_NAME]!)}
          name="lastName"
          disabled={hasEID}
          required={false}
          as={ReactTextField}
        />
      </div>

      <div className="u-margin-bottom">
        <Field
          label={translate(TRANSLATIONS[PersonalDataField.NICKNAME]!)}
          name="nickname"
          required={false}
          as={ReactTextField}
        />
      </div>

      <div className="u-margin-bottom">
        <Field name="gender">
          {({ field }: FieldProps<any>) => {
            return (
              <Select
                {...field}
                label={translate(TRANSLATIONS[PersonalDataField.GENDER]!)}
                name="gender"
                options={GENDER_OPTIONS}
                disabled={hasEID}
                required={false}
                placeholder={translate('common.select.placeholder')}
                value={field.value}
              />
            );
          }}
        </Field>
      </div>

      <div className="u-margin-bottom">
        <Field name="birthDate">
          {({ field, form }: FieldProps<any>) => {
            const [hasInvalidDate, setHasInvalidDate] = useState(
              typeof field.value === 'string' && field.value.includes('00'),
            );

            let dateValue = field.value;
            if (!hasInvalidDate && typeof field.value === 'string' && field.value !== '') {
              dateValue = parseDate(field.value);
            }

            const handleChange = (val: string, typedValue: string) => {
              // Check if val contains 00 for day and/or month
              if (typeof typedValue === 'string' && typedValue.includes('/')) {
                const [day, month] = typedValue.split('/');
                if (day === '00' || month === '00') {
                  // Validate that it matches DD/MM/YYYY pattern
                  if (/^\d{2}\/\d{2}\/\d{4}$/.test(typedValue)) {
                    setDateFieldValue(typedValue);
                    setHasInvalidDate(true);
                    form.setFieldValue('birthDate', typedValue);
                    return;
                  }
                }
              }

              const newDate = new Date(val);

              // Check if the date is valid
              if (!Number.isNaN(newDate.getTime())) {
                const day = newDate.getDate().toString().padStart(2, '0');
                const month = (newDate.getMonth() + 1).toString().padStart(2, '0');
                const year = newDate.getFullYear();

                const formattedDate = `${day}/${month}/${year}`;
                setDateFieldValue(formattedDate);
                setHasInvalidDate(false);
                form.setFieldValue('birthDate', formattedDate);
              }

              setHasInvalidDate(false);
            };

            return (
              <ReactDatepicker
                {...field}
                label={translate(TRANSLATIONS[PersonalDataField.BIRTH_DATE]!)}
                name="birthDate"
                required={false}
                invalidDateText={hasInvalidDate ? '' : undefined}
                value={dateValue}
                onChange={handleChange}
                inputProps={{ disabled: hasEID }}
              />
            );
          }}
        </Field>
      </div>
    </DataForm>
  );
};

export default PersonalData;
